import { Check, BadgeCheck, ArrowLeft, CloudUpload } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatElapsed, useFieldJob } from '../../lib/field-job.js';
import { countPendingFieldEvents, getOutboxDb } from '../../lib/outbox.js';

/**
 * Job-complete confirmation. Shows the visit KPIs and a Spire-write
 * confirmation banner. The summary is captured into state by
 * Onsite.onComplete() at the moment of clock-out so it survives a refresh.
 */
export function CompleteScreen() {
  const summary = useFieldJob((s) => s.completeSummary);
  const reset = useFieldJob((s) => s.reset);
  // Honest confirmation: captures still sitting in the offline outbox have
  // NOT been written to Spire yet — say so instead of claiming success
  // (audit P1: the banner lied while photos/signature were still queued).
  const pendingCaptures = useLiveQuery(() => countPendingFieldEvents(getOutboxDb()), [], 0);

  if (!summary) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-ink-muted">No recent completion.</div>
        <button
          type="button"
          onClick={reset}
          className="text-sm text-ink-accent hover:underline"
        >
          Back to my day
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-center">
      <div className="text-sm font-medium text-ink text-left">Complete</div>

      <div
        className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full"
        style={{ backgroundColor: 'var(--pill-pm-bg)', color: 'var(--pill-pm-fg)' }}
      >
        <Check className="h-8 w-8" aria-hidden />
      </div>
      <div>
        <div className="text-lg font-semibold text-ink">Job complete</div>
        <div className="text-sm text-ink-muted">
          {summary.customerName} · {summary.orderNo}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-left">
        <KPI label="Time on site" value={formatElapsed(summary.elapsedSec)} />
        <KPI label="Parts used" value={String(summary.partsCount)} />
        <KPI label="Photos" value={String(summary.photoCount)} />
        <KPI label="Order total (Spire)" value={`$${summary.total}`} />
      </div>

      <div
        className="rounded-md p-3 text-left flex items-start gap-2 text-sm"
        style={{
          backgroundColor: 'var(--color-customer-card-bg)',
          color: 'var(--color-customer-card-accent)',
        }}
      >
        {pendingCaptures > 0 ? (
          <CloudUpload className="h-5 w-5 shrink-0 mt-0.5" aria-hidden />
        ) : (
          <BadgeCheck className="h-5 w-5 shrink-0 mt-0.5" aria-hidden />
        )}
        <div>
          {pendingCaptures > 0 ? (
            <>
              Visit closed. {pendingCaptures} capture{pendingCaptures === 1 ? '' : 's'} (photos /
              notes / signature) still syncing to <strong>{summary.orderNo}</strong> — they upload
              automatically when you have signal.
              <div className="text-xs opacity-85 mt-0.5">
                Track them in the <Link to="/outbox" className="underline">outbox</Link>.
              </div>
            </>
          ) : (
            <>
              Signature and {summary.partsCount} part
              {summary.partsCount === 1 ? '' : 's'} written to{' '}
              <strong>{summary.orderNo}</strong> in Spire.
              <div className="text-xs opacity-85 mt-0.5">
                Photos and signature live as CRM-note attachments on the order (viewable in the
                Spire desktop UI).
              </div>
            </>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={reset}
        className="w-full inline-flex items-center justify-center gap-2 rounded-md px-3 py-3 text-sm font-semibold text-white"
        style={{ backgroundColor: 'var(--color-live-card-bg-deep)' }}
      >
        <ArrowLeft className="h-4 w-4" />
        Back to my day
      </button>
    </div>
  );
}

function KPI({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-md bg-surface px-3 py-2 border"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="text-base font-semibold text-ink tabular-nums">{value}</div>
    </div>
  );
}
